/**
 * 前端API公共类型定义
 * 统一管理各模块共用的类型定义，避免重复定义
 */

// ===== 用户相关类型 =====

/**
 * 用户组信息（嵌套在UserInfo中）
 */
export interface UserGroup {
  id: number
  name: string
  description?: string
  group_type: 'normal' | 'metalingual'
  parent_id?: number
  status: 'active' | 'inactive' | 'suspended'
  logo_path?: string
  platform_name?: string
  created_at: string
  updated_at: string
}

/** 角色摘要（列表/详情中的角色展示） */
export interface RoleSummary {
  id: number
  name: string
  code: string
}

/**
 * 用户基础信息
 */
export interface UserInfo {
  id: number
  username: string
  email: string
  name: string
  name_remark?: string
  nationality?: string
  language?: string
  language_micro?: string
  school?: string
  department?: string
  phone?: string
  user_type: 'normal' | 'restricted' | 'admin'
  group_id?: number
  group?: UserGroup  // 用户组详细信息（包含logo_path）
  status: 'active' | 'inactive' | 'suspended'
  avatar?: string
  roles?: RoleSummary[]
  created_at: string
  updated_at: string
}

/**
 * 用户摘要信息（用于引用显示）
 */
export interface UserSummary {
  id: number
  username: string
  name: string
  name_remark?: string
  language?: string
  avatar?: string
}

/**
 * 用户权限信息
 */
export interface UserPermission {
  id: number
  name: string
  code: string
  description?: string
}

// ===== 分页相关类型 =====

/**
 * 分页查询参数
 */
export interface PaginationParams {
  page?: number
  page_size?: number
  skip?: number
  limit?: number
}

/**
 * 分页响应基础结构
 */
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  pages?: number
  total_pages?: number
  has_next?: boolean
  has_prev?: boolean
}

// ===== API响应相关类型 =====

/**
 * 标准API响应格式
 */
export interface ApiResponse<T = any> {
  success: boolean
  data: T
  message?: string
  code?: number
}

/**
 * API错误响应
 */
export interface ApiError {
  success: false
  message: string
  code: number
  detail?: any
}

// ===== 状态枚举类型 =====

/**
 * 项目状态枚举
 */
export enum ProjectStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  EXPIRED = 'expired',
  CLEANED = 'cleaned'
}

/**
 * 项目类型枚举
 */
export enum ProjectType {
  VIDEO = 'video',
  DOCUMENT = 'document',
  LESSON = 'lesson'
}

/**
 * 任务状态枚举（对齐后端 TaskStatus / design.md D3）
 */
export enum TaskStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SUBMITTED = 'submitted',
  COMPLETED = 'completed',
  REJECTED_AT_ASSIGNMENT = 'rejected_at_assignment',
  /** @deprecated 旧状态机残留，勿用于新逻辑 */
  CANCELLED = 'cancelled',
  /** @deprecated */
  PAUSED = 'paused',
  /** @deprecated 请使用 SUBMITTED */
  REVIEWING = 'reviewing',
  /** @deprecated 请使用 REJECTED_AT_ASSIGNMENT */
  REJECTED = 'rejected',
}

/**
 * 任务指派方式（谁来做）— 对应委托创建时的指派范围。
 * 与 TaskPurpose（做什么）分离，避免与业务分类语义混用（Task 9.3 / D8）。
 */
export enum TaskAssignmentType {
  SELF = 'self',
  GROUP = 'group',
  METALINGUAL = 'metalingual',
}

/**
 * 任务业务目的（做什么）— 对应后端 TaskPurpose 枚举。
 */
export enum TaskPurpose {
  TRANSLATE = 'TRANSLATE',
  REVIEW = 'REVIEW',
}

/**
 * @deprecated 请使用 {@link TaskAssignmentType} 表示指派方式。
 * 保留枚举名以兼容存量 import。
 */
export enum TaskType {
  SELF = 'self',
  GROUP = 'group',
  METALINGUAL = 'metalingual',
}

/**
 * 用户类型枚举
 */
export type UserType = 'normal' | 'restricted' | 'admin'

/**
 * 用户状态枚举
 */
export type UserStatus = 'active' | 'inactive' | 'suspended'

/**
 * API类型枚举
 */
export type ApiType = 'translation' | 'speech' | 'ai_model'

/**
 * 用量类型枚举
 */
export type UsageType = 'character' | 'time'

// ===== 文件相关类型 =====

/**
 * 文件信息
 */
export interface FileInfo {
  filename: string
  file_size: number
  file_type?: string
  upload_time: string
  file_info?: Record<string, any>
}

/**
 * 文件上传响应
 */
export interface FileUploadResponse {
  filename: string
  file_url: string
  file_size: number
  file_type: string
  upload_time: string
}

// ===== 查询参数基础类型 =====

/**
 * 搜索查询参数
 */
export interface SearchParams {
  search?: string
  keyword?: string
}

/**
 * 状态筛选参数
 */
export interface StatusFilterParams {
  status?: string
}

/**
 * 时间范围查询参数
 */
export interface TimeRangeParams {
  start_time?: string
  end_time?: string
  date_from?: string
  date_to?: string
}

// ===== 统计相关类型 =====

/**
 * 基础统计信息
 */
export interface BaseStatistics {
  total: number
  active: number
  inactive: number
}

/**
 * 按类型统计
 */
export interface StatsByType {
  by_type: Record<string, number>
}

/**
 * 按日期统计
 */
export interface StatsByDate {
  by_date: Record<string, number>
}

// ===== 操作日志相关类型 =====

/**
 * 操作日志基础信息
 */
export interface ActivityLog {
  id: number
  user_id: number
  activity_type: string
  description: string
  metadata?: Record<string, any>
  created_at: string
  user?: UserSummary
}

// ===== 兼容性类型别名 =====

/**
 * 用户类型别名（向后兼容）
 */
export type User = UserInfo